import { step } from "./direction";
import type { Maze } from "./maze";
import type { Direction, Tile, Vec2 } from "./types";

/**
 * Tile-quantised movement, shared by the astronaut and the AI Slop.
 *
 * The rule both actors obey: **an actor decides only at a tile centre.** So
 * movement must land exactly on every centre it passes through, never stride
 * over one.
 *
 * Getting this wrong is not subtle. An earlier version advanced by
 * `speed * dt` and then asked "am I on a centre?" by rounding. With a step
 * size that does not divide 1 evenly the answer was *never* yes, so actors
 * never re-decided: AI Slop kept their spawn direction forever and walked
 * clean through the hull, while the astronaut happened to work only because
 * 5 tiles/s at 120 Hz is exactly 1/24 of a tile. That is a coincidence, not a
 * design, which is why the walk is now clamped explicitly.
 */

/** Positions within this of a whole number count as being on the centre. */
export const EPSILON = 1e-9;

export const onCentre = (pos: Vec2): boolean =>
  Math.abs(pos.x - Math.round(pos.x)) < EPSILON && Math.abs(pos.y - Math.round(pos.y)) < EPSILON;

/**
 * Distance from `pos` to the next tile centre along `dir`.
 *
 * Always in `(0, 1]`: standing on a centre gives a full tile, never zero, so
 * an actor always makes progress.
 */
export const gapToNextCentre = (pos: Vec2, dir: Direction): number => {
  const d = step({ x: 0, y: 0 }, dir);
  const along = d.x !== 0 ? pos.x : pos.y;
  const sign = d.x !== 0 ? d.x : d.y;
  const raw = sign > 0 ? Math.ceil(along) - along : along - Math.floor(along);
  return raw < EPSILON ? 1 : raw;
};

export interface MotionHost {
  x: number;
  y: number;
  dir: Direction;
  readonly maze: Maze;
}

/**
 * Advance one tile-step or less, landing exactly on a centre when reached.
 *
 * Returns true when the actor finished this hop standing on a tile centre,
 * which is the caller's cue to re-decide.
 */
export const hop = (host: MotionHost, budget: number): { moved: number; atCentre: boolean } => {
  const gap = gapToNextCentre(host, host.dir);
  const moved = Math.min(budget, gap);
  const d = step({ x: 0, y: 0 }, host.dir);

  if (moved >= gap - EPSILON) {
    // Snap rather than accumulate: exactness here is what makes the decision
    // point reliable, and float error would erode it over a whole level.
    const target = nextCentre(host, host.dir);
    host.x = target.x;
    host.y = target.y;
  } else {
    host.x += d.x * moved;
    host.y += d.y * moved;
  }

  wrapTunnel(host);
  return { moved, atCentre: onCentre(host) };
};

/** The tile centre immediately ahead along `dir`. */
export const nextCentre = (pos: Vec2, dir: Direction): Vec2 => {
  const d = step({ x: 0, y: 0 }, dir);
  const gap = gapToNextCentre(pos, dir);
  return { x: pos.x + d.x * gap, y: pos.y + d.y * gap };
};

/** Wrap x across the tunnel seam, preserving the offset within the tile. */
export const wrapTunnel = (host: MotionHost): void => {
  if (host.maze.tunnelRow !== Math.round(host.y)) return;
  if (host.x < -0.5) host.x += host.maze.width;
  else if (host.x > host.maze.width - 0.5) host.x -= host.maze.width;
};

/** The tile an actor occupies, wrapped through the tunnel. */
export const tileOf = (host: MotionHost): Tile =>
  host.maze.normalise({ x: Math.round(host.x), y: Math.round(host.y) });
